import {ActiveAuth, activeAuthIsNull, activeAuthKey} from "./activeAuth";
import {Subject as SubjectImpl} from "rxjs/Subject";
import {Subject} from "rxjs/Subject";
import {Observable} from "rxjs/Observable";
import {RefreshToken} from "./auth0";
import {executeNamedQuery} from './gateway';

interface ILogoutOptions {
    eventId?: string;
}

export interface ILoginManager {
    /**
     * Make sure that the initial loading of the saved auth is done
     */
    loginIfPossible(): Promise<ActiveAuth>;
    login(): Promise<ActiveAuth>;
    signUp(): Promise<ActiveAuth>;

    /**
     * Logout currently logged in activeAuth
     */
    logout(options?: ILogoutOptions): Promise<ActiveAuth>;


    /**
     * Allow consumers to listen to changes to the internal activeAuth object
     * @returns {Observable<ActiveAuth>}
     */
    activeAuthObservable(): Observable<ActiveAuth>;

    isLoggedIn(): Promise<boolean>;

    /**
     * Get userId of logged in user or null
     * @returns {Promise<string>}
     */
    getUserId(): Promise<string | null>
};

class LoginManager implements ILoginManager {


    private _lastActiveAuth: ActiveAuth;
    private _lastUserId: string;
    private _activeAuthSubject: Subject<ActiveAuth>;

    constructor() {
        this._activeAuthSubject = new SubjectImpl();
        this._tryToSwitch_commitMemory(new ActiveAuth(null), null)
    }

    activeAuthObservable() {
        return this._activeAuthSubject;
    }

    /**
     * Switch the activeAuth and broadcast to subject
     * @param {ActiveAuth} target
     * @returns {Promise<ActiveAuth>}
     * @private
     */
    _tryToSwitch_commitMemory(target: ActiveAuth, userId: string): Promise<ActiveAuth> {
        this._lastActiveAuth = target;
        this._lastUserId = userId;
        this._activeAuthSubject.next(target);
        return Promise.resolve(target);
    }

    /**
     * Save ActiveAuth to persistent store
     * @param {ActiveAuth} target
     * @returns {Promise<ActiveAuth>}
     * @private
     */
    _tryToSwitch_commitStore(target: ActiveAuth): Promise<ActiveAuth> {
        return target.save()
            .then(() => {return target});
    }

    _tryToSwitch(activeAuth: ActiveAuth): Promise<ActiveAuth> {

        if (!activeAuth) {
            activeAuth = new ActiveAuth(new RefreshToken(null))
        }

        const currentKey = activeAuthKey(this._lastActiveAuth);
        const newKey = activeAuthKey(activeAuth);

        var promise = Promise.resolve(this._lastActiveAuth);
        var userId = null;

        //Short-circuit this process in case there is nothing to change
        if (!!this._lastActiveAuth && currentKey == newKey) {
            return promise;
        }

        promise = promise.then(() => {

            if (!newKey) {
                return activeAuth;
            }

            const getAccessToken = new Promise<ActiveAuth>((resolve, reject) => {
                activeAuth.getAccessToken(function (err, accessToken) {
                    if (err) {
                        reject(err);
                        return;
                    }

                    const decodedAccessToken = accessToken && accessToken.decode();

                    if (!decodedAccessToken) {
                        reject(new Error("Could not decode token"));
                    }

                    const subject = decodedAccessToken.sub;
                    if (!subject) {
                        reject(new Error("Token for unknown userId"));
                    }

                    userId = subject;
                    resolve(activeAuth);
                    return;
                });
            });

            return getAccessToken;
        });

        return promise
            .then(() => this._tryToSwitch_commitMemory(activeAuth, userId))
            .then(() => this._tryToSwitch_commitStore(activeAuth));
    }

    _redirectToRealLogin(redirectToSignUp): Promise<ActiveAuth> {
        const search = `?prev=${encodeURIComponent(window.location.pathname + window.location.search)}`.replace(/%/g, "~");
        const pathname = redirectToSignUp ? `/signup` : `/login`;
        window.location.replace(`${window.location.origin}${pathname}${search}`);
        return Promise.reject(new Error());
    }

    loginIfPossible(): Promise<ActiveAuth> {
        const tryTarget = ActiveAuth.fromSavedData();
        return this._tryToSwitch(tryTarget);
    }

    login(): Promise<ActiveAuth> {
        return this.loginIfPossible()
            .then((activeAuth) => {
                if (activeAuthIsNull(activeAuth)) {
                    return this._redirectToRealLogin(false);
                }
                return activeAuth;
            });
    }

    signUp(): Promise<ActiveAuth> {
        return this.loginIfPossible()
            .then((activeAuth) => {
                if (activeAuthIsNull(activeAuth)) {
                    return this._redirectToRealLogin(true);
                }
                return activeAuth;
            });
    }

    async logout(options?: ILogoutOptions): Promise<ActiveAuth> {
        const activeAuth = await this._tryToSwitch(null);

        // notify the Gateway
        const eventId = options && options.eventId;
        const variables = { eventId }; // logout from this Event
        await executeNamedQuery(activeAuth, 'logoutEventSession', variables);

        return activeAuth;
    }

    isLoggedIn(): Promise<boolean> {
        return this.loginIfPossible()
            .then((activeAuth) => {
                return !activeAuthIsNull(activeAuth);
            });
    }

    getUserId(): Promise<string | null> {
        return this.loginIfPossible()
            .then(() => {
                return this._lastUserId;
            })
    }

}

export const loginManager: ILoginManager = new LoginManager();
